<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import MediaLibraryAPI from 'dashboard/api/mediaLibrary';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
const props = defineProps({
  product: { type: Object, default: null },
  taxonomy: { type: Object, required: true },
});
const emit = defineEmits(['saved', 'cancel', 'busy']);
const { t } = useI18n();
const draft = ref({
  name: '',
  category_id: '',
  product_class_id: '',
  ...props.product,
});
draft.value.category_id ||= '';
draft.value.product_class_id ||= '';
const saving = ref(false);
const error = ref('');
const options = entries => [
  { value: '', label: t('MEDIA_LIBRARY.NONE') },
  ...entries.map(entry => ({ value: entry.id, label: entry.name })),
];
async function save() {
  if (!draft.value.name.trim() || saving.value) return;
  saving.value = true;
  emit('busy', true);
  error.value = '';
  try {
    const { data } = await MediaLibraryAPI.saveTaxonomy('products', {
      id: draft.value.id,
      name: draft.value.name.trim(),
      category_id: draft.value.category_id || null,
      product_class_id: draft.value.product_class_id || null,
    });
    emit('saved', data);
  } catch {
    error.value = t('MEDIA_LIBRARY.TAXONOMY.SAVE_ERROR');
  } finally {
    saving.value = false;
    emit('busy', false);
  }
}
</script>

<template>
  <fieldset
    :disabled="saving"
    class="m-0 flex min-w-0 flex-col gap-4 border-0 p-4"
  >
    <label class="flex flex-col gap-1.5 text-xs text-n-slate-11">
      {{ t('MEDIA_LIBRARY.TAXONOMY.NAME') }}
      <input
        v-model="draft.name"
        type="text"
        maxlength="200"
        required
        class="!mb-0 !h-10 !w-full !rounded-lg !border-n-weak !bg-n-field !px-3 !py-2 !text-sm !text-n-slate-12"
        @keydown.enter.prevent="save"
      />
    </label>
    <div class="flex flex-col gap-1.5">
      <span class="text-xs text-n-slate-11">{{
        t('MEDIA_LIBRARY.CATEGORY')
      }}</span>
      <ComboBox
        v-model="draft.category_id"
        :options="options(taxonomy.categories)"
        :allow-deselect="false"
        :disabled="saving"
        :aria-label="t('MEDIA_LIBRARY.CATEGORY')"
      />
      <p class="m-0 text-xs text-n-slate-11">
        {{ t('MEDIA_LIBRARY.CATEGORY_HELP') }}
      </p>
    </div>
    <div class="flex flex-col gap-1.5">
      <span class="text-xs text-n-slate-11">{{
        t('MEDIA_LIBRARY.PRODUCT_CLASS')
      }}</span>
      <ComboBox
        v-model="draft.product_class_id"
        :options="options(taxonomy.product_classes)"
        :allow-deselect="false"
        :disabled="saving"
        :aria-label="t('MEDIA_LIBRARY.PRODUCT_CLASS')"
      />
      <p class="m-0 text-xs text-n-slate-11">
        {{ t('MEDIA_LIBRARY.CLASS_HELP') }}
      </p>
    </div>
    <p v-if="error" role="alert" class="m-0 text-sm text-n-ruby-11">
      {{ error }}
    </p>
    <div class="flex justify-end gap-2 border-t border-n-weak pt-4">
      <Button
        variant="outline"
        color="slate"
        :label="t('MEDIA_LIBRARY.CANCEL')"
        :disabled="saving"
        @click="emit('cancel')"
      />
      <Button
        icon="i-lucide-save"
        :label="t('MEDIA_LIBRARY.SAVE')"
        :disabled="saving || !draft.name.trim()"
        :is-loading="saving"
        @click="save"
      />
    </div>
  </fieldset>
</template>
